import { IAnimationRenderer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/slide-transition/render/interface';
import { TransitionTarget } from '@src/slide/domain/ui-model/element-modeles/transition/constants';
import { PlayerRenderService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player';
import { BrowserCanvas } from '@tencent/alloy-renderer-browser';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { getColorMatrixFilter, GlBuffer, glColorMatrixFilter } from '@tencent/alloy-renderer';
import { IAnimationUpdateParams, ITransitionHandlerOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/slide-transition/interface';

export class FlashRenderer implements IAnimationRenderer {
    private options: ITransitionHandlerOptions;
    private enterCanvas?: BrowserCanvas;
    private exitCanvas?: BrowserCanvas;
    private buffer: BrowserCanvas | GlBuffer | null;
    public constructor(options: ITransitionHandlerOptions) {
        this.options = options;
    }
    public beforeAnimation = () => {
        const renderTree = PlayerRenderService.getInstance().getRenderTree(this.options.slideModel);
        const prevSlideModel = this.options.prevSlideModel ?? this.options.getPrevSlideModel?.();

        if (this.options.enterCanvas || this.options.exitCanvas) {
            this.enterCanvas = this.options.enterCanvas;
            this.exitCanvas = this.options.exitCanvas;
        } else {
            if (this.options.isSwitchForward?.()) {
                if (prevSlideModel) {
                    const prevRenderTree = PlayerRenderService.getInstance().getRenderTree(prevSlideModel);
                    this.enterCanvas = prevRenderTree?.getApplication()?.view;
                }
                this.exitCanvas = renderTree?.getApplication()?.view;
            } else {
                this.enterCanvas = renderTree?.getApplication()?.view;
                if (prevSlideModel) {
                    const renderTree = PlayerRenderService.getInstance().getRenderTree(prevSlideModel);
                    this.exitCanvas = renderTree?.getApplication()?.view;
                }
            }
        }

        if (this.enterCanvas) {
            this.buffer = !FORBID_WEBGL_FILTER ? this.enterCanvas.getGlBuffer(
                this.enterCanvas.width / this.enterCanvas.resolution,
                this.enterCanvas.height / this.enterCanvas.resolution,
            ) : this.enterCanvas.createBuffer();
        }
        if (this.buffer?.native) {
            const exitWrapper = this.options.getExitWrapper?.();
            exitWrapper?.appendChild(this.buffer.native as HTMLCanvasElement);
        }

        this.options.beforeAnimation?.(true);
    };

    public renderAnimationFrame = (params: IAnimationUpdateParams) => {
        const { target, style } = params;

        this.options.renderAnimationFrame?.(params, true);

        if (target === TransitionTarget.ENTER && style.progress === 1) {
            return;
        }
        const canvas = target === TransitionTarget.ENTER ? this.enterCanvas : this.exitCanvas;
        if (!canvas) {
            if (this.buffer instanceof BrowserCanvas) {
                this.buffer.rect(0, 0, this.buffer.width, this.buffer.height);
                const color = ((style.progress ?? 0) * 255).toFixed(0);
                this.buffer.nativeFillStyle = `rgba(${color}, ${color}, ${color}, 1)`;
                this.buffer.fill();
            }
            return;
        }
        const progress = (style.progress ?? 0) * 100;
        const brightness = progress === 0 ? 1 : progress !== 100 ? (1 / (1 - (progress / 100))) : 100;
        this.renderCanvas(canvas, brightness);
    };

    public afterAnimation = () => {
        this.options.afterAnimation?.(true);

        const exitWrapper = this.options.getExitWrapper?.();
        if (exitWrapper && exitWrapper?.childNodes?.length > 0) {
            exitWrapper.removeChild(exitWrapper.childNodes[0]);
        }
    };

    private renderCanvas(canvas: BrowserCanvas, brightness: number) {
        if (!this.buffer) {
            return;
        }
        const matrix: number[] = [
            brightness, 0, 0, 0, 0,
            0, brightness, 0, 0, 0,
            0, 0, brightness, 0, 0,
            0, 0, 0, 1, 0,
        ];

        if (!FORBID_WEBGL_FILTER) {
            glColorMatrixFilter(this.buffer as GlBuffer, canvas.native as TexImageSource, matrix);
        } else {
            const filterId = getColorMatrixFilter(
                canvas.width,
                canvas.height,
                `${matrix[0]} ${matrix[1]} ${matrix[2]} ${matrix[3]} ${matrix[4]}
                ${matrix[5]} ${matrix[6]} ${matrix[7]} ${matrix[8]} ${matrix[9]}
                ${matrix[10]} ${matrix[11]} ${matrix[12]} ${matrix[13]} ${matrix[14]}
                ${matrix[15]} ${matrix[16]} ${matrix[17]} ${matrix[18]} ${matrix[19]}`,
            );
            (this.buffer as BrowserCanvas).filter = `url(#${filterId})`;
            (this.buffer as BrowserCanvas).drawCanvas(canvas, canvas.padding.left, canvas.padding.top);
        }
    }
}
